import { useState } from "react";
import {
  Box,
  Switch,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

const glassCard = {
  p: 2,
  borderRadius: 3,
  bgcolor: "rgba(255,255,255,0.7)",
  backdropFilter: "blur(8px)",
  border: "1px solid rgba(229,231,235,0.6)",
  boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
};

const DynamicQuestionTab = ({ skipOption, setSkipOption, settings, setSettings, accentColor = "#6366f1" }) => {
  const s = settings || {};

  const accentDark = (() => {
    if (!accentColor.startsWith("#") || accentColor.length !== 7) return accentColor;
    const n = parseInt(accentColor.slice(1), 16);
    const r = Math.max(0, (n >> 16) - 30);
    const g = Math.max(0, ((n >> 8) & 0xff) - 30);
    const b = Math.max(0, (n & 0xff) - 30);
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
  })();

  const update = (patch) => setSettings({ ...s, ...patch });

  const [testStatus, setTestStatus] = useState("idle");
  const [testOptions, setTestOptions] = useState([]);
  const [testError, setTestError] = useState("");

  const headersList = Array.isArray(s.headers) ? s.headers : [];
  const addHeader = () => update({ headers: [...headersList, { key: "", value: "" }] });
  const changeHeader = (i, field, val) => {
    const u = [...headersList];
    u[i] = { ...u[i], [field]: val };
    update({ headers: u });
  };
  const removeHeader = (i) => update({ headers: headersList.filter((_, idx) => idx !== i) });

  const buildHeadersObj = () => {
    const obj = { "Content-Type": "application/json" };
    headersList.forEach((h) => {
      if (h.key && h.key.trim()) obj[h.key] = h.value || "";
    });
    return obj;
  };

  const extractOptions = (data) => {
    let list = Array.isArray(data) ? data : data.options || data.data || data.results || [];
    const path = (s.responsePath || "").trim();
    if (path && path !== "auto") {
      path.split(".").forEach((p) => {
        if (list && typeof list === "object") list = list[p];
      });
    }
    if (!Array.isArray(list)) return [];
    return list.map((o) =>
      typeof o === "object" ? o[s.labelField || "label"] || o[s.valueField || "value"] || o.name || String(o) : String(o)
    );
  };

  const handleTest = async () => {
    if (!s.apiEndpoint) {
      setTestStatus("error");
      setTestError("API endpoint is empty");
      return;
    }
    setTestStatus("loading");
    setTestError("");
    setTestOptions([]);
    try {
      const fetchOpts = {
        method: s.apiMethod || "GET",
        headers: buildHeadersObj(),
      };
      if (s.apiMethod === "POST" && s.apiBody) {
        try {
          fetchOpts.body = JSON.stringify(JSON.parse(s.apiBody));
        } catch {
          setTestStatus("error");
          setTestError("Invalid JSON in body");
          return;
        }
      }
      const res = await fetch(s.apiEndpoint, fetchOpts);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const opts = extractOptions(data);
      if (opts.length === 0) {
        setTestStatus("error");
        setTestError("API returned no options");
        return;
      }
      setTestOptions(opts);
      setTestStatus("success");
    } catch (err) {
      setTestStatus("error");
      setTestError(err.message || "Request failed");
    }
  };

  const insertVariable = (varname) => {
    const current = s.apiBody || "";
    update({ apiBody: current + (current ? " " : "") + varname });
  };

  return (
    <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
      {/* ─── Skip Option ─── */}
      <Box sx={glassCard}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1f2937" }}>Skip Option</Typography>
            <Typography sx={{ fontSize: 11, color: "#6b7280" }}>Allow visitors to skip this question</Typography>
          </Box>
          <Switch checked={!!skipOption} onChange={(e) => setSkipOption(e.target.checked)} size="small" />
        </Box>
      </Box>

      {/* ─── 1. API Source ─── */}
      <Box sx={glassCard}>
        <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1, color: "#1f2937" }}>API Source</Typography>
        <Typography sx={{ fontSize: 11, color: "#6b7280", mb: 1.5 }}>
          Options fetch from this endpoint at runtime. Supports variables like {"{Q1}"}, {"{city}"}.
        </Typography>

        <TextField
          fullWidth
          size="small"
          label="API Endpoint"
          placeholder="https://api.example.com/categories"
          value={s.apiEndpoint || ""}
          onChange={(e) => update({ apiEndpoint: e.target.value })}
          sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff", fontSize: 12 } }}
        />

        <Typography sx={{ fontWeight: 600, fontSize: 12, mb: 0.8, color: "#374151" }}>Method</Typography>
        <ToggleButtonGroup
          value={s.apiMethod || "GET"}
          exclusive
          size="small"
          fullWidth
          onChange={(_, v) => v && update({ apiMethod: v })}
          sx={{
            mb: 2,
            "& .MuiToggleButton-root": { textTransform: "none", fontSize: 11, fontWeight: 600, flex: 1 },
          }}
        >
          <ToggleButton value="GET">GET</ToggleButton>
          <ToggleButton value="POST">POST</ToggleButton>
        </ToggleButtonGroup>

        {/* Headers */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 12, color: "#374151" }}>Headers (optional)</Typography>
          <Box
            onClick={addHeader}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              color: accentColor,
              "&:hover": { color: accentDark },
            }}
          >
            <AddIcon sx={{ fontSize: 14 }} />
            <Typography sx={{ fontSize: 11, fontWeight: 600 }}>Add</Typography>
          </Box>
        </Box>
        {headersList.map((h, i) => (
          <Box key={i} sx={{ display: "flex", gap: 0.8, mb: 1, alignItems: "center" }}>
            <TextField
              size="small"
              placeholder="Header name (e.g. Authorization)"
              value={h.key}
              onChange={(e) => changeHeader(i, "key", e.target.value)}
              sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff", fontSize: 12 } }}
            />
            <TextField
              size="small"
              placeholder="Value (e.g. Bearer xyz)"
              value={h.value}
              onChange={(e) => changeHeader(i, "value", e.target.value)}
              sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff", fontSize: 12 } }}
            />
            <IconButton
              size="small"
              onClick={() => removeHeader(i)}
              sx={{ color: "#ef4444", "&:hover": { bgcolor: "#fef2f2" } }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        ))}

        {/* Body (POST only) */}
        {s.apiMethod === "POST" && (
          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.8 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 12, color: "#374151" }}>Request Body (JSON)</Typography>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                {["{Q1}", "{Q2}", "{name}", "{email}"].map((v) => (
                  <Chip
                    key={v}
                    label={v}
                    size="small"
                    onClick={() => insertVariable(v)}
                    sx={{
                      height: 20,
                      fontSize: 10,
                      fontWeight: 600,
                      bgcolor: "#ede9fe",
                      color: "#6d28d9",
                      cursor: "pointer",
                      "&:hover": { bgcolor: "#ddd6fe" },
                    }}
                  />
                ))}
              </Box>
            </Box>
            <TextField
              fullWidth
              size="small"
              multiline
              rows={3}
              placeholder='{"user_id": "{Q1}"}'
              value={s.apiBody || ""}
              onChange={(e) => update({ apiBody: e.target.value })}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff", fontSize: 12, fontFamily: "monospace" } }}
            />
          </Box>
        )}

        <TextField
          fullWidth
          size="small"
          type="number"
          label="Timeout (seconds)"
          value={s.timeout ?? 10}
          onChange={(e) => update({ timeout: Number(e.target.value) || 10 })}
          sx={{ mt: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff", fontSize: 12 } }}
        />
      </Box>

      {/* ─── 2. Response Mapping ─── */}
      <Box sx={glassCard}>
        <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1, color: "#1f2937" }}>Response Mapping</Typography>
        <Typography sx={{ fontSize: 11, color: "#6b7280", mb: 1.5 }}>
          Tell the bot where to find options in the API response.
        </Typography>

        <TextField
          fullWidth
          size="small"
          label="Response path"
          placeholder='auto (or e.g. "data.items")'
          value={s.responsePath || ""}
          onChange={(e) => update({ responsePath: e.target.value })}
          sx={{
            mb: 1.5,
            "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff", fontSize: 12 },
          }}
        />

        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
          <TextField
            fullWidth
            size="small"
            label="Label field"
            placeholder="label"
            value={s.labelField || ""}
            onChange={(e) => update({ labelField: e.target.value })}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff", fontSize: 12 } }}
          />
          <TextField
            fullWidth
            size="small"
            label="Value field"
            placeholder="value"
            value={s.valueField || ""}
            onChange={(e) => update({ valueField: e.target.value })}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff", fontSize: 12 } }}
          />
        </Box>

        <Box sx={{ p: 1.2, bgcolor: "#f8fafc", borderRadius: 1.5, border: "1px solid #e2e8f0" }}>
          <Typography sx={{ fontSize: 11, color: "#475569", fontWeight: 600, mb: 0.5 }}>Supported response shapes:</Typography>
          <Typography sx={{ fontSize: 10, color: "#64748b", fontFamily: "monospace" }}>• ["A", "B", "C"]</Typography>
          <Typography sx={{ fontSize: 10, color: "#64748b", fontFamily: "monospace" }}>• {"{ options: [...] }"}</Typography>
          <Typography sx={{ fontSize: 10, color: "#64748b", fontFamily: "monospace" }}>• {"{ data: [{label, value}] }"}</Typography>
          <Typography sx={{ fontSize: 10, color: "#64748b", fontFamily: "monospace" }}>• {"{ results: [...] }"}</Typography>
        </Box>
      </Box>

      {/* ─── 3. Test API ─── */}
      <Box sx={glassCard}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 14, color: "#1f2937" }}>Test API</Typography>
            <Typography sx={{ fontSize: 11, color: "#6b7280" }}>Verify the endpoint before saving</Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={testStatus === "loading" ? <CircularProgress size={12} /> : <PlayArrowIcon />}
            onClick={handleTest}
            disabled={testStatus === "loading"}
            sx={{ textTransform: "none", fontSize: 11, fontWeight: 600, borderRadius: 2 }}
          >
            {testStatus === "loading" ? "Testing..." : "Run Test"}
          </Button>
        </Box>

        {testStatus === "error" && (
          <Box sx={{ p: 1.2, bgcolor: "#fef2f2", borderRadius: 1.5, border: "1px solid #fecaca" }}>
            <Typography sx={{ fontSize: 11, color: "#b91c1c", fontWeight: 600 }}>Error: {testError}</Typography>
          </Box>
        )}

        {testStatus === "success" && (
          <Box>
            <Typography sx={{ fontSize: 11, color: "#15803d", fontWeight: 600, mb: 0.8 }}>
              Success — {testOptions.length} option{testOptions.length === 1 ? "" : "s"} loaded
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {testOptions.slice(0, 10).map((o, i) => (
                <Chip
                  key={i}
                  label={o}
                  size="small"
                  sx={{ bgcolor: "#dcfce7", color: "#15803d", fontSize: 11, fontWeight: 600 }}
                />
              ))}
              {testOptions.length > 10 && (
                <Chip
                  label={`+${testOptions.length - 10} more`}
                  size="small"
                  sx={{ bgcolor: "#f3f4f6", color: "#6b7280", fontSize: 11 }}
                />
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* ─── 4. Display & Behavior ─── */}
      <Box sx={glassCard}>
        <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1.5, color: "#1f2937" }}>Display & Behavior</Typography>

        <Typography sx={{ fontWeight: 600, fontSize: 12, mb: 0.8, color: "#374151" }}>Show as</Typography>
        <ToggleButtonGroup
          value={s.style || "button"}
          exclusive
          size="small"
          fullWidth
          onChange={(_, v) => v && update({ style: v })}
          sx={{
            mb: 2,
            "& .MuiToggleButton-root": { textTransform: "none", fontSize: 11, fontWeight: 600, flex: 1 },
          }}
        >
          <ToggleButton value="button">Buttons</ToggleButton>
          <ToggleButton value="dropdown">Dropdown</ToggleButton>
          <ToggleButton value="list">List</ToggleButton>
        </ToggleButtonGroup>

        <TextField
          fullWidth
          size="small"
          type="number"
          label="Max options to show"
          value={s.maxOptions ?? 20}
          onChange={(e) => update({ maxOptions: Number(e.target.value) || 20 })}
          sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff", fontSize: 12 } }}
        />

        <TextField
          fullWidth
          size="small"
          label="Fallback message (if API fails)"
          placeholder="Sorry, couldn't load options. Please try again."
          value={s.fallbackMessage || ""}
          onChange={(e) => update({ fallbackMessage: e.target.value })}
          sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff", fontSize: 12 } }}
        />

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1.2, bgcolor: "#f9fafb", borderRadius: 2, border: "1px solid #e5e7eb" }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 12, color: "#1f2937" }}>Retry on failure</Typography>
            <Typography sx={{ fontSize: 10, color: "#6b7280" }}>Auto-retry once if first request fails</Typography>
          </Box>
          <Switch
            checked={!!s.retryOnFailure}
            onChange={(e) => update({ retryOnFailure: e.target.checked })}
            size="small"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default DynamicQuestionTab;
